import { QueryKeys } from "@/constants/Query";
import { withMultipleEncountersHandler } from "@/features/Patient/MedicalRecords/MedicalHistory/useMedicalHistoryErrorHandler";
import { type ApiError, useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { icd10SearchPath } from "@/network/icd10SearchEndpoint";
import { Condition as CuraiCondition } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type { Condition } from "fhir/r4";
import { useCallback } from "react";

export interface Icd10Code {
  id: string; // "M10.10"
  term: string; // "Lead-induced gout, unspecified site"
  system: string; // "http://hl7.org/fhir/sid/icd-10-cm"
  version: string; // "ICD-10-CM 2023"
  score: number; // 43.33499
  level?: number; // 0 for root, 1 for first level, 2 for second level, etc.
  billable?: boolean; // true if the code is billable, false otherwise
}

export interface Icd10CodeResponse {
  matchedConcepts: Icd10Code[];
}

export const useIcd10CodeSearch = (patientId: string, searchTerm?: string) => {
  const fetchIcd10Codes = useCallback(async () => {
    const response = await axiosInstance.get<Icd10CodeResponse>(
      icd10SearchPath({ nested: true }),
      {
        params: {
          searchTerm: searchTerm ? searchTerm : undefined,
        },
      },
    );
    return response.data;
  }, [searchTerm]);

  return useApi<Icd10CodeResponse>({
    queryOptions: {
      enabled: Boolean(searchTerm && searchTerm.length > 2),
      queryFn: fetchIcd10Codes,
      queryKey: [
        ...QueryKeys.Patient.Chart.Billing.ICD_10_CODES({
          patientId,
          searchTerm,
        }),
      ],
    },
  });
};

type StrictCoding = {
  code: string;
  system: string;
  display: string;
  version: string;
};

export type StrictCodeableConcept = {
  coding: StrictCoding[];
  text: string;
};

export interface CptCodesResponse {
  cptCodes: StrictCodeableConcept[];
  suggestedCode: StrictCodeableConcept;
}

export const useCptSearch = (
  patientId: string,
  patientType: "new" | "established" | null,
  billType: "duration" | "mdm",
  encounterTimeMinutes: number,
  visitType: "av" | "chat" | null,
) => {
  const fetchCpt = useCallback(async () => {
    if (!patientType || !visitType) {
      throw new Error("Patient type and visit type are required");
    }

    const params: Record<string, string | number> = {
      patientType,
      billType,
      encounterTimeMinutes,
      visitType,
    };

    const response = await axiosInstance.get<CptCodesResponse>(
      "/medical-coding/cpt-codes/",
      { params },
    );
    return response.data;
  }, [patientType, billType, encounterTimeMinutes, visitType]);

  return useApi<CptCodesResponse>({
    queryOptions: {
      enabled: patientType !== null && visitType !== null,
      queryFn: fetchCpt,
      queryKey: QueryKeys.Patient.Chart.Billing.CPT_CODES({
        patientId,
        patientType,
        billType,
        encounterTimeMinutes,
        visitType,
      }),
    },
  });
};

interface ClaimSubmitPayload {
  additionalBillingCodes?: StrictCodeableConcept[];
  motherEncounterId: string;
  billableChildEncounters: {
    childEncounterId: string;
    icd10Codes: StrictCoding[];
  }[];
  overriddenEncounterTime: number;
  overriddenAvCallTime: number;
  postEncounterActivities: {
    activityType:
      | "work-note"
      | "review-additional-records"
      | "referral"
      | "imaging-order"
      | "ma-coordination";
    time: number;
  }[];
  primaryIcd10Code: StrictCodeableConcept;
  cptCode: StrictCodeableConcept;
  billType: "duration" | "mdm";
  visitType: "av" | "chat";
  finalized: boolean;
  patient: {
    id: string;
    userId: number;
    partnerUid: number;
    isDependent: boolean;
    type: "new" | "established";
  };
}

export const useClaimSubmit = (
  patientId: string,
  encounterId: string,
  onSuccess: () => void,
  onError: (error: Error) => void,
) => {
  const queryClient = useQueryClient();
  const submit = useCallback(
    async (payload: ClaimSubmitPayload) => {
      const response = await axiosInstance.post(
        `/medical-coding/claim-submit/encounter/${encounterId}`,
        payload,
      );
      return response.data;
    },
    [encounterId],
  );

  return useApiMutation({
    mutationOptions: {
      mutationFn: submit,
      mutationKey: QueryKeys.Patient.Chart.Billing.CLAIM_SUBMIT({
        patientId,
        encounterId,
      }),
      onSuccess: () => {
        // Invalidate the available actions query to avoid
        // unnecessarily showing the billing modal again
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Distribution.AVAILABLE_ACTIONS({
            patientId,
            encounterId,
          }),
        });
        onSuccess();
      },
      onError: onError,
    },
  });
};

export const useStartEncounterTime = (
  patientId: string,
  encounterId: string,
) => {
  const queryClient = useQueryClient();
  const startEncounterTime = useCallback(async () => {
    const response = await axiosInstance.post(
      `/medical-coding/encounter-time/encounter/${encounterId}/charting`,
    );
    return response.data;
  }, [encounterId]);

  return useApiMutation({
    mutationOptions: {
      mutationFn: startEncounterTime,
      mutationKey: QueryKeys.Patient.Chart.Billing.START_ENCOUNTER_TIME({
        patientId,
        encounterId,
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Billing.ENCOUNTER_CODING({
            patientId,
            encounterId,
          }),
        });
      },
    },
  });
};

export const useCompleteEncounterTime = (
  patientId: string,
  encounterId: string,
) => {
  const queryClient = useQueryClient();
  const completeEncounterTime = useCallback(async () => {
    const response = await axiosInstance.put(
      `/medical-coding/encounter-time/encounter/${encounterId}/complete`,
    );
    return response.data;
  }, [encounterId]);

  return useApiMutation({
    mutationOptions: {
      mutationFn: completeEncounterTime,
      mutationKey: QueryKeys.Patient.Chart.Billing.COMPLETE_ENCOUNTER_TIME({
        patientId,
        encounterId,
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Billing.ENCOUNTER_CODING({
            patientId,
            encounterId,
          }),
        });
      },
    },
  });
};

interface EncounterTimeResponse {
  chartingTimeSpent: number;
  othersChartingTimeSpent: number;
  avCallTimeSpent: number;
  postEncounterActivities: {
    activityType:
      | "work-note"
      | "review-additional-records"
      | "referral"
      | "imaging-order"
      | "ma-coordination";
    displayName: string;
    time: number;
  }[];
  patientType: "new" | "established";
  billType: "duration" | "mdm";
  suggestedVisitType: "av" | "chat";
  suggestedVisitTypeDisplayName: string;
}

export interface EncounterCodingResponse {
  visitDiagnoses?: Condition[] | null;
  childEncounters: {
    id: string;
    displayName: string;
    episodeOfCareId: string;
    conditionCodeValue: string;
    icd10Codes: {
      code: string;
      display: string;
      label: string;
      isOrderCode: boolean;
      value: StrictCoding;
    }[];
  }[];
  encounterTimeDetails: EncounterTimeResponse;
}

export interface BatchAddDxFromVisitDxVariables {
  encounterDiagnosisConditionIds: string[];
}

export interface BatchAddDxFromVisitDxResponse {
  conditions: Condition[];
}

export type DiagnosisHistoryRelevance = "MATCH" | "PARENT" | "CHILD";
export type VisitDiagnosisCondition = CuraiCondition & { id: string };

export interface VisitDiagnosesRelevantHistoryResponse {
  visitDiagnoses: {
    condition: VisitDiagnosisCondition;
    relevantMedicalHistory: {
      condition: VisitDiagnosisCondition;
      relevance: DiagnosisHistoryRelevance;
    }[];
  }[];
}

interface RawVisitDiagnosesRelevantHistoryResponse {
  visitDiagnoses: {
    condition: Condition;
    relevantMedicalHistory: {
      condition: Condition;
      relevance: DiagnosisHistoryRelevance;
    }[];
  }[];
}

export const useVisitDiagnosesRelevantHistory = (
  patientId: string,
  encounterId: string,
) =>
  useApi<
    RawVisitDiagnosesRelevantHistoryResponse,
    VisitDiagnosesRelevantHistoryResponse
  >({
    queryOptions: {
      enabled: Boolean(patientId && encounterId),
      refetchOnMount: "always",
      staleTime: 0,
      queryFn: async () => {
        const response =
          await axiosInstance.get<RawVisitDiagnosesRelevantHistoryResponse>(
            `/charting/encounter-charting/${encounterId}/diagnoses/relevant-history`,
          );

        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.VisitClosure.RELEVANT_DIAGNOSIS_HISTORY(
        {
          patientId,
          encounterId,
        },
      ),
      select: (response) => ({
        visitDiagnoses: response.visitDiagnoses.map((visitDiagnosis) => ({
          condition: new CuraiCondition(
            visitDiagnosis.condition,
          ) as VisitDiagnosisCondition,
          relevantMedicalHistory: visitDiagnosis.relevantMedicalHistory.map(
            (history) => ({
              condition: new CuraiCondition(
                history.condition,
              ) as VisitDiagnosisCondition,
              relevance: history.relevance,
            }),
          ),
        })),
      }),
    },
  });

export const useBatchAddDxFromVisitDx = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: (response: BatchAddDxFromVisitDxResponse) => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    BatchAddDxFromVisitDxResponse,
    BatchAddDxFromVisitDxVariables
  >({
    mutationOptions: {
      mutationFn: async (variables) => {
        const response =
          await axiosInstance.post<BatchAddDxFromVisitDxResponse>(
            "/charting/medical-history/condition/batch-add-from-encounter-diagnoses",
            variables,
          );

        return response.data;
      },
      mutationKey:
        QueryKeys.Patient.Chart.VisitClosure.BATCH_ADD_DX_FROM_VISIT_DX(
          patientId,
        ),
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
            patientId,
          }),
        });
        onSuccess?.(response);
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useEncounterCoding = (
  patientId: string,
  encounterId: string,
  enabled = true,
) => {
  const fetchEncounterCoding = useCallback(async () => {
    const response = await axiosInstance.get<EncounterCodingResponse>(
      `/medical-coding/encounter-coding/${encounterId}`,
    );
    return response.data;
  }, [encounterId]);

  return useApi<EncounterCodingResponse>({
    queryOptions: {
      queryFn: fetchEncounterCoding,
      queryKey: QueryKeys.Patient.Chart.Billing.ENCOUNTER_CODING({
        patientId,
        encounterId,
      }),
      enabled: Boolean(patientId && encounterId) && enabled,
    },
  });
};
